import { FC, useState } from 'react';
import { StudentSkillStates } from '../../models/StudentSkillState';
import { useAppSelector } from '../../redux/hooks';
import { selectedUnit } from '../../redux/reducers/unitSlice';
import { selectActiveFocusUnit } from '../../redux/reducers/focusSlice';
import { selectUser } from '../../redux/reducers/userSlice';
import { isSkillCompleted } from '../../businessLogicService/skillCompletionUtils';
import { getStudentInitials } from '../../businessLogicService/getStudentInitials';
import { TargetSkillTypeLabel } from '../../models/TargetSkillType';
import { UnitHeaderTitle } from './UnitHeaderTitle';
import { UnitSkillBadgeRow } from './UnitSkillBadgeRow';
import { AnimatedFocusPill } from '../UnitFocus/AnimatedFocusPill';
import { ChevronDown } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useUnitPathSegment } from '../../contexts/UnitPathSegmentContext';
import { UnitPathSegmentIndicator } from './UnitPathSegmentIndicator';
import { scrollToUnitPathPosition, useUnitPathPositionAnchor } from '../../custom-hooks/useUnitPathPositionAnchor';
import {
  resolveUnitBaseColor,
  unitProgressBarColor,
} from '../../businessLogicService/StudentDashboardAux/getColorByUnit';
import { getUnitHeaderChrome } from '../../businessLogicService/StudentDashboardAux/getUnitHeaderChrome';

interface Props {
  pathToSkills: StudentSkillStates[];
  foundationSkills: StudentSkillStates[];
}

const EXPANDABLE_REGION_ID = 'dashboard-unit-header-details';

export const DashboardUnitHeader: FC<Props> = ({ pathToSkills, foundationSkills }) => {
  const unit = useAppSelector(selectedUnit);
  const focusUnit = useAppSelector(selectActiveFocusUnit);
  const student = useAppSelector(selectUser);
  const reduceMotion = useReducedMotion();
  const [expanded, setExpanded] = useState<boolean>(false);
  const { headerRef, segments, activeIndex, activeSegment, scrollToSegment, remeasureHeader } = useUnitPathSegment();
  // Same reading the rail's "Back to position" button uses, so both controls show and hide together.
  const { isOutOfView: positionOutOfView } = useUnitPathPositionAnchor();
  const focused = !!focusUnit && focusUnit.id === unit?.id;
  const foundationsAllCompleted = foundationSkills.length > 0 && foundationSkills.every(isSkillCompleted);
  const targetSkills = pathToSkills.filter(skill => skill.targetSkillType === TargetSkillTypeLabel.TARGET);
  const supportSkills = [...pathToSkills, ...foundationSkills].filter(
    (skill, index, arr) => skill.targetSkillType !== TargetSkillTypeLabel.TARGET && arr.indexOf(skill) === index,
  );
  const targetCompleted = targetSkills.filter(isSkillCompleted).length;
  const supportCompleted = supportSkills.filter(isSkillCompleted).length;
  const allTargetsCompleted = targetSkills.length > 0 && targetCompleted === targetSkills.length;
  // With every Target passed the header switches to the centered summary layout and loses its toggle, so a
  // leftover `expanded` must not keep the details panel alive.
  const detailsExpanded = expanded && !allTargetsCompleted;
  const headerChrome = getUnitHeaderChrome(unit?.name, { completed: allTargetsCompleted, focused });
  const segmentColor = unitProgressBarColor[resolveUnitBaseColor(unit?.name)];
  const previousSegment = activeIndex > 0 ? segments[activeIndex - 1] : null;
  const nextSegment = activeIndex >= 0 && activeIndex < segments.length - 1 ? segments[activeIndex + 1] : null;
  const badgeRow = (
    <UnitSkillBadgeRow
      foundationsAllCompleted={foundationsAllCompleted}
      supportCompleted={supportCompleted}
      targetCompleted={targetCompleted}
    />
  );

  return (
    <div
      ref={headerRef}
      className={`tw-w-full tw-relative tw-flex tw-flex-col tw-px-8 tw-py-5 tw-rounded-3xl tw-border-4 tw-border-solid ${headerChrome.className}`}
      style={headerChrome.style}
    >
      {focused && (
        <div className="tw-absolute tw--top-3 tw-left-4 tw-z-10 tw-flex tw-items-center">
          <AnimatedFocusPill />
        </div>
      )}

      {/* Once every Target is complete there is nothing left to navigate to, so the header becomes a
          centered summary — title above, badges centered below it — drops the expand toggle, and takes
          the unit's own colors (see getUnitHeaderChrome). */}
      {allTargetsCompleted ? (
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
          <UnitHeaderTitle centered />
          {badgeRow}
        </div>
      ) : (
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-6">
          <UnitHeaderTitle />

          <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-6">
            {badgeRow}
            <button
              type="button"
              onClick={() => setExpanded(current => !current)}
              aria-expanded={detailsExpanded}
              aria-controls={EXPANDABLE_REGION_ID}
              aria-label={detailsExpanded ? 'Collapse unit details' : 'Expand unit details'}
              className="tw-flex tw-cursor-pointer tw-items-center tw-border-none tw-bg-transparent tw-p-0"
            >
              <ChevronDown
                className={`tw-h-6 tw-w-6 tw-text-neutral-500 tw-transition-transform tw-duration-200 ${
                  detailsExpanded ? 'tw-rotate-180' : ''
                }`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      )}

      {/* Collapsing shrinks the header back, so refresh the reading line's cached height once the exit
          animation has removed the panel — the expand side is handled by onAnimationComplete below. */}
      <AnimatePresence initial={false} onExitComplete={remeasureHeader}>
        {detailsExpanded && (
          <motion.div
            id={EXPANDABLE_REGION_ID}
            role="region"
            aria-label="Unit details"
            className="tw-overflow-hidden"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={reduceMotion ? { duration: 0 } : { duration: 0.25, ease: 'easeInOut' }}
            // Capture the settled header height for the scroll-spy reading line once expand finishes.
            onAnimationComplete={remeasureHeader}
          >
            <div className="tw-mt-4 tw-pb-2 tw-pt-4" style={{ borderTop: '2px solid #F1F0EE' }}>
              {activeSegment && (
                <UnitPathSegmentIndicator
                  label={activeSegment.label}
                  name={activeSegment.name}
                  completed={activeSegment.completed}
                  total={activeSegment.total}
                  color={segmentColor}
                  studentInitials={getStudentInitials(student)}
                  studentPicture={student?.picture}
                  previous={
                    previousSegment
                      ? { label: previousSegment.label, onSelect: () => scrollToSegment(previousSegment.key) }
                      : undefined
                  }
                  next={
                    nextSegment
                      ? { label: nextSegment.label, onSelect: () => scrollToSegment(nextSegment.key) }
                      : undefined
                  }
                  onBackToPosition={() => scrollToUnitPathPosition(true)}
                  isPositionOutOfView={positionOutOfView}
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
